import {useDeletePipelineMutation,} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UsePipelineUpdateProps = {}

export type UsePipelineUpdateResult = {
  deletePipeline: (pipelineId: string, onSuccess: (() => void) | undefined) => void
}

export const usePipelineUpdate = (props: UsePipelineUpdateProps = {}): UsePipelineUpdateResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_deletePipeline, {isLoading: isDeleting}] = useDeletePipelineMutation()

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "usePipelineUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.pipeline", "Pipeline"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "usePipelineUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.pipeline", "Pipeline"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("usePipelineUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.pipeline", "Pipeline") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((pipelineId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deletePipeline({id: pipelineId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deletePipeline, handleDeleteResult])

  const deletePipeline = useCallback((pipelineId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.pipeline", "Pipeline"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.pipeline.confirm", "Are you sure you want to delete this pipeline?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(pipelineId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    deletePipeline: deletePipeline,
  }
}
